/**
 * The auth gate (`111-frontend-architecture.md` §7).
 *
 * It BLOCKS on the session query and renders a full-page skeleton while
 * `GET /api/auth/me` is in flight — **never a redirect flash**. That is M1's
 * acceptance criterion #4: a hard reload keeps the session and the app boots into the
 * shell without showing the login screen on the way.
 */
import { Navigate, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/features/auth/useSession';
import { useTranslation } from '@/lib/i18n';
import { hadSession } from '@/stores/lastSession';

function BootSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col" aria-busy="true" aria-label={t('auth.boot.loading')}>
      <div className="flex h-8 shrink-0 items-center gap-2 border-b border-[var(--border)] px-2">
        <Skeleton className="h-3 w-40" />
      </div>
      <div className="flex flex-1">
        <div className="w-[260px] shrink-0 space-y-1 border-r border-[var(--border)] bg-[var(--muted)] p-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[var(--row-h)] w-full" />
          ))}
        </div>
        <div className="flex-1 space-y-3 p-6">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data: session, isPending, isError, error, refetch } = useSession();
  const { t } = useTranslation();

  if (isPending) return <BootSkeleton />;

  // Not a 401 — a 401 resolves to `null` below. This is the backend being DOWN, and
  // bouncing the user to a login screen they cannot use would be a lie about why.
  if (isError) {
    // If this browser had a session the last time it reached the server, boot the shell
    // anyway (`stores/lastSession.ts` explains why this is safe, and why it grants
    // nothing). The user's unsaved draft is in IndexedDB RIGHT NOW; refusing to render
    // means showing them an error page instead of their own words, and `112` §1 lists
    // "the backend is down / restarting / mid-deploy" as an ORDINARY failure the app must
    // simply survive. The editor loads from the draft, typing continues, and the outbox
    // lands it when the server comes back.
    if (hadSession()) return children;

    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="font-medium">{t('auth.offline.title')}</p>
        <p className="text-[var(--muted-foreground)]">{error.message}</p>
        <Button
          variant="outline"
          onClick={() => {
            void refetch();
          }}
        >
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (session === null) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return children;
}
